/**
 * The success leg of a Shopify connect, read from a URL.
 *
 * The OAuth callback (server routes/integrations/shopify/oauthRoutes.ts) lands
 * a connected store on one of two pages, and names the store on both:
 *
 *   /settings/integrations?shopify=success&shop=<domain>&name=<store name>
 *   /onboarding?platform=shopify&success=true&shop=<domain>&name=<store name>
 *
 * (the second for a workspace still in the setup wizard). When the browser has
 * no session, either one is first carried through the sign-in page as
 * `?redirect=`, and that page reads it back from there.
 */
export interface ShopifyReturn {
  shop: string | null;
  name: string | null;
}

/** The params the success and error legs add, which the Integrations page clears once read. */
const RETURN_PARAMS = ['shopify', 'reason', 'shop', 'name'] as const;

function nonEmpty(value: string | null): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

/**
 * The store named by a same-site path that is a Shopify success landing, or
 * null for any other path. Callers pass a path they have already restricted to
 * this site; anything unparseable is simply not a Shopify return.
 */
export function shopifyReturnIn(path: string): ShopifyReturn | null {
  let params: URLSearchParams;
  try {
    params = new URL(path, window.location.origin).searchParams;
  } catch {
    return null;
  }
  const isReturn =
    params.get('shopify') === 'success' ||
    (params.get('platform') === 'shopify' && params.get('success') === 'true');
  if (!isReturn) return null;
  return { shop: nonEmpty(params.get('shop')), name: nonEmpty(params.get('name')) };
}

/**
 * A copy of `params` without the Shopify return params. Everything else stays:
 * the Integrations page renders inside the Account hub, whose `?tab=` must
 * survive the cleanup.
 */
export function withoutShopifyReturnParams(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of RETURN_PARAMS) next.delete(key);
  return next;
}
